import { injectable } from 'inversify';

import { HttpService } from '@vk-hr-tek/core/http';
import { ValidationService } from '@vk-hr-tek/core/validation';
import { Calendar } from '@vk-hr-tek/core/calendar';
import { UnmarshallerService } from '@vk-hr-tek/core/unmarshaller';

import {
  CreatePolicyResponse,
  GetGovDocumentTypeOptionsResponse,
  GetPolicyCompanies as CompaniesResponse,
} from '@app/gen/policy';

import { CreatePolicyDto } from '../dto';

@injectable()
export class PolicyCreateService {
  constructor(
    private validator: ValidationService,
    private calendar: Calendar,
    private http: HttpService,
    private unmarshaller: UnmarshallerService,
  ) {}

  async getCompanies() {
    return this.unmarshaller.unmarshall(
      await this.http.get('/policy/companies', {}, { withSide: true }),
      CompaniesResponse,
    );
  }

  async getGovDocumentOptions() {
    const result = await this.unmarshaller.unmarshall(
      await this.http.get(
        '/policy/gov_document_type/options',
        {},
        {
          withSide: true,
        },
      ),
      GetGovDocumentTypeOptionsResponse,
    );
    return result;
  }

  async createPolicy(createPolicyDto: CreatePolicyDto) {
    await this.validator.validateOrReject(createPolicyDto, CreatePolicyDto);

    const response = await this.unmarshaller.unmarshall(
      await this.http.post(
        '/policy',
        {
          name: createPolicyDto.name,
          company_id: createPolicyDto.companyId,
          ...(createPolicyDto.unitType
            ? { unit_type: createPolicyDto.unitType }
            : {}),
          ...(createPolicyDto.includeUnitManagers
            ? { include_unit_managers: createPolicyDto.includeUnitManagers }
            : {}),
          ...(createPolicyDto.unitIds
            ? { unit_ids: createPolicyDto.unitIds }
            : {}),
          ...(createPolicyDto.activeTo
            ? {
                active_to: this.calendar.formatISO(
                  this.calendar.endOfDay(new Date(createPolicyDto.activeTo)),
                ),
              }
            : {}),
          active_from: this.calendar.formatISO(
            this.calendar.startOfDay(new Date(createPolicyDto.activeFrom)),
          ),
          ...(createPolicyDto.acceptancePeriodDays && {
            acceptance_period_days: createPolicyDto.acceptancePeriodDays,
          }),
          document_date: createPolicyDto.documentDate,
          ...(createPolicyDto.documentNumber && {
            document_number: createPolicyDto.documentNumber,
          }),
          signature_type: createPolicyDto.signatureType,
          with_order: createPolicyDto.withOrder,
          gov_document_type_code: createPolicyDto.govDocumentTypeCode,
          positions: createPolicyDto.positions,
          ...(createPolicyDto.employees
            ? { employee_ids: createPolicyDto.employees }
            : {}),
          ...(createPolicyDto.hideIfAccepted && {
            hide_if_accepted: createPolicyDto.hideIfAccepted,
          }),
          ...(createPolicyDto?.files && {
            files: createPolicyDto?.files.map((file) =>
              typeof file?.value === 'string'
                ? JSON.parse(file?.value as string).id
                : file?.value,
            ),
          }),
          ...(createPolicyDto.additionalFiles
            ? {
                attached_file_ids: createPolicyDto.additionalFiles,
              }
            : {}),
          ...(createPolicyDto.categoryId && {
            category_id: createPolicyDto.categoryId,
          }),
          ...(createPolicyDto.childCompanies
            ? { create_for_child_companies: createPolicyDto.childCompanies }
            : {}),
        },
        {
          withSide: true,
          isJson: false,
        },
      ),
      CreatePolicyResponse,
    );

    return {
      policy_id: response.policy_id,
    };
  }
}
